import React, { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Check, Loader2, Plus, X } from 'lucide-react'
import { MainLayout } from '@/components/layout/MainLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'
import BrandCreateStep from '@/components/invitation/BrandCreateStep'
import { MatchPreview } from '@/components/setup/MatchPreview'
import { GiftingBonusEditor } from '@/components/gifting/GiftingBonusEditor'
import { suggestedBonuses, type BonusTier } from '@adthlete/common/utils/dealPricing'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { DEAL_TYPE_OPTIONS, type DealType } from '@/lib/dealTypes'
import {
  BUDGETS,
  GENDERS,
  POPULAR_SPORTS,
  REGIONS,
  US_STATES,
  brandSetupItems,
  brandSetupScore,
  lookingForOf,
  AGE_GROUPS,
  type LookingFor,
} from '@/lib/brandProfile'
import { fetchCurrentUser } from '@/redux/userSlice'
import type { AppDispatch } from '@/store'
import {
  useGetBrandsQuery,
  useGetSportsQuery,
  useUpdateOrganizationMutation,
} from '@/redux/marketerApi'

const Chip: React.FC<{
  active: boolean
  onClick: () => void
  children: React.ReactNode
  small?: boolean
}> = ({ active, onClick, children, small }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'rounded-full border font-semibold transition-colors',
      small ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm',
      active
        ? 'border-[#C0FD72] bg-[#C0FD72] text-black'
        : 'border-foreground/15 text-gray-800 hover:bg-foreground/10',
    )}
  >
    {children}
  </button>
)

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <section className="rounded-xl border border-foreground/10 bg-card p-5 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
    <h2 className="text-base font-bold text-foreground">{title}</h2>
    {hint ? <p className="mt-0.5 text-sm text-gray-600">{hint}</p> : null}
    <div className="mt-4">{children}</div>
  </section>
)

const toggle = <T,>(list: T[], v: T) =>
  list.includes(v) ? list.filter((x) => x !== v) : [...list, v]

/**
 * B2: brand setup. The brand (name and logo), what it is about, and what it is looking for:
 * deal types, sports, places, athletes, budget. The meter and the live match preview sit beside
 * the form on desktop and under it on a phone. New orgs land here first; later it is the brand
 * profile, reachable from the dashboard and the account menu.
 */
const BrandSetup: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const dispatch = useDispatch<AppDispatch>()
  const { toast } = useToast()
  const { marketer } = useAuth()
  const org = (marketer as any)?.org
  const { data: brands = [], refetch: refetchBrands } = useGetBrandsQuery()
  const { data: sports = [] } = useGetSportsQuery()
  const [save, { isLoading: saving }] = useUpdateOrganizationMutation()
  const [lf, setLf] = useState<LookingFor>(() => lookingForOf(org))
  const [bonuses, setBonuses] = useState<BonusTier[] | null>(
    () => org?.preferences?.gifting_bonuses ?? null,
  )
  const [addingBrand, setAddingBrand] = useState(false)
  const [statesOpen, setStatesOpen] = useState(false)
  const [sportText, setSportText] = useState('')

  // Quick picks, plus any other sport already chosen so it can be removed
  const popular = sports.filter((s) => POPULAR_SPORTS.includes(s.name))
  const pickedOthers = sports.filter(
    (s) => lf.sports.includes(s.id) && !POPULAR_SPORTS.includes(s.name),
  )

  /** A typed sport we know becomes a pick (and counts in the match); anything else is kept as written */
  const addSport = () => {
    const name = sportText.trim()
    if (!name) return
    const known = sports.find((s) => s.name.toLowerCase() === name.toLowerCase())
    setLf((p) =>
      known
        ? { ...p, sports: p.sports.includes(known.id) ? p.sports : [...p.sports, known.id] }
        : {
            ...p,
            other_sports: p.other_sports.some((o) => o.toLowerCase() === name.toLowerCase())
              ? p.other_sports
              : [...p.other_sports, name.slice(0, 40)],
          },
    )
    setSportText('')
  }
  const firstTime = params.get('first') === '1' || !org?.preferences?.looking_for

  const draftOrg = useMemo(
    () => ({ ...org, brandCount: brands.length, preferences: { looking_for: lf } }),
    [org, brands.length, lf],
  )
  const items = brandSetupItems(draftOrg)
  const score = brandSetupScore(draftOrg)

  const byRegion = useMemo(
    () =>
      REGIONS.map((region) => ({
        region,
        states: US_STATES.filter((s) => s.region === region),
      })),
    [],
  )
  const regionAll = (region: string) =>
    US_STATES.filter((s) => s.region === region).every((s) => lf.states.includes(s.code))
  const toggleRegion = (region: string) => {
    const codes = US_STATES.filter((s) => s.region === region).map((s) => s.code)
    setLf((p) => ({
      ...p,
      states: regionAll(region)
        ? p.states.filter((c) => !codes.includes(c))
        : [...new Set([...p.states, ...codes])],
    }))
  }

  const submit = async () => {
    try {
      await save({
        looking_for: lf,
        ...(lf.deal_types.includes('gifting')
          ? { gifting_bonuses: bonuses ?? suggestedBonuses() }
          : {}),
      }).unwrap()
      await dispatch(fetchCurrentUser())
      toast({ title: 'Brand profile saved', description: `${score}% complete.` })
      navigate(firstTime ? '/setup/connect' : '/')
    } catch (err: any) {
      toast({
        title: "Couldn't save",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  return (
    <MainLayout>
      <div className="container mx-auto max-w-6xl p-4 sm:p-6">
        <div className="mb-6">
          {firstTime ? (
            <p className="text-xs font-bold uppercase tracking-wider text-[#3F7A0A]">
              Step 1 of 2 · Brand setup
            </p>
          ) : null}
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {firstTime ? 'Tell athletes who you are' : 'Your brand profile'}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Athletes see your brand when you post opportunities and book their content. What you're
            looking for sets the default audience for every opportunity and powers your matches.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Form */}
          <div className="min-w-0 space-y-4">
            <Section
              title="Your brand"
              hint="The name and logo athletes see. An agency can add more than one."
            >
              {brands.length ? (
                <ul className="flex flex-wrap gap-3">
                  {brands.map((b) => (
                    <li
                      key={b.id}
                      className="flex items-center gap-2 rounded-lg border border-foreground/10 bg-card py-1.5 pl-1.5 pr-3"
                    >
                      <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-muted text-xs font-bold text-foreground">
                        {b.logo ? (
                          <img src={b.logo} alt="" className="h-full w-full object-cover" />
                        ) : (
                          b.name.slice(0, 2).toUpperCase()
                        )}
                      </span>
                      <span className="text-sm font-semibold text-foreground">{b.name}</span>
                    </li>
                  ))}
                  {!addingBrand ? (
                    <li>
                      <button
                        type="button"
                        onClick={() => setAddingBrand(true)}
                        className="flex h-full items-center gap-1.5 rounded-lg border border-dashed border-foreground/20 px-3 py-2 text-sm text-gray-700 hover:bg-foreground/10"
                      >
                        <Plus className="h-4 w-4" /> Add a brand
                      </button>
                    </li>
                  ) : null}
                </ul>
              ) : null}
              {brands.length === 0 || addingBrand ? (
                <div
                  className={cn(brands.length ? 'mt-4 border-t border-foreground/10 pt-4' : '')}
                >
                  <BrandCreateStep
                    onCreated={async () => {
                      setAddingBrand(false)
                      await refetchBrands()
                      await dispatch(fetchCurrentUser())
                    }}
                    onBack={brands.length ? () => setAddingBrand(false) : undefined}
                  />
                </div>
              ) : null}
            </Section>

            <Section
              title="What your brand is about"
              hint="One or two sentences. Athletes read this."
            >
              <Textarea
                value={lf.about ?? ''}
                onChange={(e) => setLf((p) => ({ ...p, about: e.target.value }))}
                rows={3}
                maxLength={500}
                placeholder="We make recovery drinks for college athletes who train twice a day."
                className="border-foreground/15 bg-card text-base text-foreground placeholder:text-gray-500"
              />
            </Section>

            <Section
              title="What you want to run"
              hint="Pick everything you'd do. Each campaign can mix them."
            >
              <div className="grid gap-2">
                {DEAL_TYPE_OPTIONS.map((d) => {
                  const on = lf.deal_types.includes(d.id)
                  return (
                    <button
                      key={d.id}
                      type="button"
                      aria-pressed={on}
                      title={d.blurb}
                      onClick={() =>
                        setLf((p) => ({
                          ...p,
                          deal_types: toggle(p.deal_types, d.id as DealType),
                        }))
                      }
                      className={cn(
                        'flex items-start gap-3 rounded-lg border px-4 py-3 text-left',
                        on
                          ? 'border-[#C0FD72] bg-[#C0FD72]/10'
                          : 'border-foreground/15 hover:bg-foreground/10',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                          on ? 'border-[#C0FD72] bg-[#C0FD72] text-black' : 'border-foreground/30',
                        )}
                      >
                        {on ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground">
                          {d.label}
                        </span>
                        <span className="block text-xs text-gray-600">Also called: {d.aka}</span>
                        <span className="mt-1 block text-xs text-gray-700">{d.blurb}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </Section>

            {lf.deal_types.includes('gifting') ? (
              <Section
                title="Gifting: how it works"
                hint="You send product and athletes post. Set the bonuses athletes earn when their posts take off; you can change them per campaign."
              >
                <GiftingBonusEditor value={bonuses} onChange={(t) => setBonuses(t)} />
              </Section>
            ) : null}

            <Section
              title="Who your brand is for"
              hint="Your customer, in your words. We match athletes whose audience looks like them."
            >
              <input
                value={lf.customer ?? ''}
                onChange={(e) => setLf((p) => ({ ...p, customer: e.target.value }))}
                maxLength={300}
                placeholder="Women 35 to 70 who run and care about recovery"
                aria-label="Who your brand is for"
                className="h-12 w-full rounded-lg border border-foreground/15 bg-card px-4 text-base text-foreground placeholder:text-gray-500 outline-none focus:border-[#C0FD72]"
              />
              <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-gray-600">
                Their ages
              </p>
              <div className="flex flex-wrap gap-2">
                {AGE_GROUPS.map((a) => (
                  <Chip
                    key={a.id}
                    active={lf.ages.includes(a.id)}
                    onClick={() => setLf((p) => ({ ...p, ages: toggle(p.ages, a.id) }))}
                  >
                    {a.label}
                  </Chip>
                ))}
              </div>
              <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-gray-600">
                Mostly
              </p>
              <div className="flex flex-wrap gap-2">
                {GENDERS.map((g) => (
                  <Chip
                    key={g.id}
                    active={lf.gender === g.id}
                    onClick={() => setLf((p) => ({ ...p, gender: g.id as LookingFor['gender'] }))}
                  >
                    {g.label}
                  </Chip>
                ))}
              </div>
            </Section>

            <Section
              title="Sports (optional)"
              hint="Only if you want certain sports. Most brands leave this empty and let the audience match decide."
            >
              <div className="flex flex-wrap gap-2">
                {popular.map((s) => (
                  <Chip
                    key={s.id}
                    active={lf.sports.includes(s.id)}
                    onClick={() => setLf((p) => ({ ...p, sports: toggle(p.sports, s.id) }))}
                  >
                    {s.name}
                  </Chip>
                ))}
              </div>
              <form
                className="mt-4 flex max-w-md gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  addSport()
                }}
              >
                <Input
                  value={sportText}
                  onChange={(e) => setSportText(e.target.value)}
                  list="sport-names"
                  placeholder="Add a sport, like Swimming or Esports"
                  aria-label="Add a sport"
                  className="h-10 border-foreground/15 bg-card text-base text-foreground placeholder:text-gray-500"
                />
                <datalist id="sport-names">
                  {sports.map((s) => (
                    <option key={s.id} value={s.name} />
                  ))}
                </datalist>
                <Button
                  type="submit"
                  variant="dark-outline"
                  className="h-10"
                  disabled={!sportText.trim()}
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </form>
              {pickedOthers.length || lf.other_sports.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pickedOthers.map((s) => (
                    <span
                      key={s.id}
                      className="inline-flex items-center gap-1 rounded-full border border-[#C0FD72] bg-[#C0FD72] py-1 pl-3 pr-1.5 text-sm font-semibold text-black"
                    >
                      {s.name}
                      <button
                        type="button"
                        aria-label={`Remove ${s.name}`}
                        onClick={() =>
                          setLf((p) => ({ ...p, sports: p.sports.filter((id) => id !== s.id) }))
                        }
                        className="rounded-full p-0.5 hover:bg-black/10"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                  {lf.other_sports.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center gap-1 rounded-full border border-[#C0FD72] bg-[#C0FD72] py-1 pl-3 pr-1.5 text-sm font-semibold text-black"
                    >
                      {name}
                      <button
                        type="button"
                        aria-label={`Remove ${name}`}
                        onClick={() =>
                          setLf((p) => ({
                            ...p,
                            other_sports: p.other_sports.filter((o) => o !== name),
                          }))
                        }
                        className="rounded-full p-0.5 hover:bg-black/10"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
              {lf.other_sports.length ? (
                <p className="mt-2 text-xs text-gray-500">
                  Sports we don't list yet are saved with your profile but don't count in your
                  match.
                </p>
              ) : null}
            </Section>

            <Section
              title="Where your athletes are"
              hint="A region picks all of its states. Leave it empty for anywhere."
            >
              <div className="flex flex-wrap gap-2">
                {REGIONS.map((r) => (
                  <Chip key={r} active={regionAll(r)} onClick={() => toggleRegion(r)}>
                    {r}
                  </Chip>
                ))}
                <button
                  type="button"
                  onClick={() => setStatesOpen((v) => !v)}
                  className="px-2 text-sm text-gray-600 underline-offset-2 hover:text-foreground hover:underline"
                >
                  {statesOpen ? 'Hide states' : 'Pick states'}
                  {lf.states.length ? ` (${lf.states.length})` : ''}
                </button>
              </div>
              {statesOpen ? (
                <div className="mt-4 space-y-3">
                  {byRegion.map(({ region, states }) => (
                    <div key={region}>
                      <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-gray-500">
                        {region}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {states.map((s) => (
                          <Chip
                            key={s.code}
                            small
                            active={lf.states.includes(s.code)}
                            onClick={() =>
                              setLf((p) => ({ ...p, states: toggle(p.states, s.code) }))
                            }
                          >
                            {s.name}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </Section>

            <Section
              title="What you usually pay"
              hint="What you usually pay per deliverable, like one Reel or one TikTok. Pick every range that fits."
            >
              <div className="grid gap-2 sm:grid-cols-2">
                {BUDGETS.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setLf((p) => ({ ...p, budgets: toggle(p.budgets, b.id) }))}
                    aria-pressed={lf.budgets.includes(b.id)}
                    className={cn(
                      'flex items-center justify-between rounded-lg border px-4 py-3 text-left',
                      lf.budgets.includes(b.id)
                        ? 'border-[#C0FD72] bg-[#C0FD72]/10'
                        : 'border-foreground/15 hover:bg-foreground/10',
                    )}
                  >
                    <span>
                      <span className="block text-sm font-semibold text-foreground">
                        {b.label}
                      </span>
                      <span className="block text-xs text-gray-600">{b.blurb}</span>
                    </span>
                    {lf.budgets.includes(b.id) ? (
                      <Check className="h-4 w-4 text-[#3F7A0A]" />
                    ) : null}
                  </button>
                ))}
              </div>
            </Section>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button variant="lime" className="h-11 px-6" disabled={saving} onClick={submit}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {firstTime ? 'Save and continue' : 'Save'}
              </Button>
              {firstTime ? (
                <Button asChild variant="ghost" className="text-gray-700">
                  <Link to="/setup/connect">Skip for now</Link>
                </Button>
              ) : (
                <Button asChild variant="ghost" className="text-gray-700">
                  <Link to="/">Cancel</Link>
                </Button>
              )}
            </div>
          </div>

          {/* Meter and match */}
          <aside className="order-first space-y-4 lg:order-none lg:sticky lg:top-6 lg:self-start">
            <section className="rounded-xl border border-foreground/10 bg-card p-5 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
              <div className="flex items-end justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Brand profile
                </p>
                <p className="text-2xl font-bold tabular-nums text-foreground">{score}%</p>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-[#C0FD72] transition-all"
                  style={{ width: `${score}%` }}
                />
              </div>
              <ul className="mt-4 space-y-1.5">
                {items.map((i) => (
                  <li
                    key={i.id}
                    className={cn(
                      'flex items-center gap-2 text-sm',
                      i.done ? 'text-foreground' : 'text-gray-500',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-full border',
                        i.done
                          ? 'border-[#C0FD72] bg-[#C0FD72] text-black'
                          : 'border-foreground/20',
                      )}
                    >
                      {i.done ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                    </span>
                    {i.label}
                  </li>
                ))}
              </ul>
            </section>
            <MatchPreview sports={lf.sports} states={lf.states} gender={lf.gender} />
          </aside>
        </div>
      </div>
    </MainLayout>
  )
}

export default BrandSetup
